<script setup lang="ts">
/**
 * AppHeader Component
 * Main application header with logo, user menu, and role indicator
 */

import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Avatar from 'primevue/avatar'
import Menu from 'primevue/menu'
import Button from 'primevue/button'
import Drawer from 'primevue/drawer'
import type { User } from '@/types'
import { getNavigationSections, isNavigationItemActive } from '@/utils/navigation'

interface Props {
  user?: User | null
  showNavigation?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  user: null,
  showNavigation: true,
})

const emit = defineEmits<{
  logout: []
  menuToggle: []
  openContact: []
}>()

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const userMenu = ref<InstanceType<typeof Menu> | null>(null)
const isMenuOpen = ref(false)
const isMobileMenuOpen = ref(false)
const headerEl = ref<HTMLElement | null>(null)
const logoSrc = '/branding/pg-health-contigo-logo.png'

// Publish header height as CSS variable so sticky nav can track it
let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  if (headerEl.value) {
    resizeObserver = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty(
        '--header-height',
        `${entry.borderBoxSize[0].blockSize}px`,
      )
    })
    resizeObserver.observe(headerEl.value)
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

// User display info
const userInitials = computed(() => {
  if (!props.user) return '?'
  const first = props.user.name?.charAt(0) || ''
  const last = props.user.surname?.charAt(0) || ''
  return (first + last).toUpperCase() || '?'
})

const userDisplayName = computed(() => {
  if (!props.user) return ''
  return `${props.user.name} ${props.user.surname}`
})

const roleLabel = computed(() => {
  if (!props.user) return ''
  return props.user.role === 'owner' ? t('header.roleOwner') : t('header.rolePharmacist')
})

// Navigation items for mobile menu
const mobileNavSections = computed(() =>
  props.user ? getNavigationSections(props.user.role) : []
)

// Check if route is active
function isRouteActive(itemRoute: string) {
  return isNavigationItemActive(route.path, itemRoute)
}

// Navigate from mobile menu
function navigateTo(name: string) {
  router.push({ name })
  isMobileMenuOpen.value = false
}

// User menu items
const userMenuItems = computed(() => [
  {
    label: t('header.myProfile'),
    icon: 'pi pi-user',
    command: () => router.push({ name: 'Profile' }),
  },
  {
    separator: true,
  },
  {
    label: t('header.logout'),
    icon: 'pi pi-sign-out',
    command: () => emit('logout'),
  },
])

// Toggle user menu
function toggleUserMenu(event: Event) {
  isMenuOpen.value = !isMenuOpen.value
  userMenu.value?.toggle(event)
}

// Handle keyboard navigation
function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    toggleUserMenu(event)
  }
}

// Mobile menu toggle
function handleMenuToggle() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  emit('menuToggle')
}
</script>

<template>
  <header ref="headerEl" class="app-header">
    <div class="header-container">
      <div class="header-content">
        <!-- Left: Logo and mobile menu -->
        <div class="flex items-center gap-3">
          <!-- Mobile menu button -->
          <Button
            v-if="showNavigation"
            icon="pi pi-bars"
            text
            rounded
            class="lg:hidden mobile-menu-btn"
            :aria-label="t('nav.menu')"
            @click="handleMenuToggle"
          />

          <!-- Logo -->
          <router-link to="/" class="logo-link">
            <img
              :src="logoSrc"
              :alt="t('header.logoAlt')"
              class="logo-image"
            />
            <div class="logo-text">
              <span class="logo-brand">P&amp;G Health</span>
              <span class="logo-product">Contigo Incentivos</span>
            </div>
          </router-link>
        </div>

        <!-- Right: User section -->
        <div v-if="user" class="flex items-center gap-3">
          <!-- Role badge -->
          <span class="role-badge" :class="user.role === 'owner' ? 'role-owner' : 'role-pharmacist'">
            <i :class="user.role === 'owner' ? 'pi pi-star-fill' : 'pi pi-user'" aria-hidden="true"></i>
            {{ roleLabel }}
          </span>

          <!-- Contáctanos trigger -->
          <button
            type="button"
            class="contact-header-btn"
            :aria-label="t('header.openContact')"
            @click="emit('openContact')"
          >
            <i class="pi pi-envelope" aria-hidden="true"></i>
            <span class="hidden sm:block">{{ t('header.contact') }}</span>
          </button>

          <!-- User avatar and menu -->
          <button
            type="button"
            class="user-menu-btn"
            :aria-expanded="isMenuOpen"
            aria-haspopup="menu"
            :aria-label="t('header.userMenu')"
            @click="toggleUserMenu"
            @keydown="handleKeyDown"
          >
            <Avatar
              :label="userInitials"
              shape="circle"
              class="user-avatar"
              :aria-hidden="true"
            />
            <span class="hidden sm:block user-name">
              {{ userDisplayName }}
            </span>
            <i class="pi pi-chevron-down text-xs" aria-hidden="true"></i>
          </button>

          <Menu
            ref="userMenu"
            :model="userMenuItems"
            :popup="true"
            @hide="isMenuOpen = false"
          />
        </div>

        <!-- Login button for guests -->
        <div v-else>
          <Button
            :label="t('header.login')"
            icon="pi pi-sign-in"
            class="login-btn"
            size="small"
            @click="router.push({ name: 'Login' })"
          />
        </div>
      </div>
    </div>

    <!-- Mobile navigation drawer -->
    <Drawer
      v-model:visible="isMobileMenuOpen"
      :header="t('nav.navigation')"
      position="left"
      class="w-72"
    >
      <nav class="flex flex-col gap-5">
        <section v-for="section in mobileNavSections" :key="section.key" class="flex flex-col gap-1">
          <p class="px-4 text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">
            {{ t(section.labelKey) }}
          </p>
          <button
            v-for="item in section.items"
            :key="item.name"
            class="flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors w-full"
            :class="isRouteActive(item.route)
              ? 'bg-pg-blue/10 text-pg-blue font-medium'
              : 'text-gray-700 hover:bg-gray-100'"
            @click="navigateTo(item.name)"
          >
            <i :class="item.icon" class="text-lg"></i>
            <span>{{ t(item.labelKey) }}</span>
          </button>
        </section>
      </nav>

      <template #footer>
        <div class="border-t pt-4">
          <Button
            :label="t('header.logout')"
            icon="pi pi-sign-out"
            severity="secondary"
            text
            class="w-full"
            @click="emit('logout'); isMobileMenuOpen = false"
          />
        </div>
      </template>
    </Drawer>
  </header>
</template>

<style scoped>
.app-header {
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-container {
  background: linear-gradient(135deg, #003DA5 0%, #0052D4 50%, #1a5fcf 100%);
  box-shadow: 0 4px 20px rgba(0, 61, 165, 0.3);
}

.header-content {
  max-width: 80rem;
  margin: 0 auto;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Logo */
.logo-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  transition: transform 0.2s ease;
}

.logo-link:hover {
  transform: scale(1.02);
}

.logo-image {
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.92);
  padding: 0.2rem;
}

.logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.logo-brand {
  font-size: 1rem;
  font-weight: 800;
  color: white;
  letter-spacing: -0.02em;
}

.logo-product {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

/* Role badges */
.role-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.role-owner {
  background: linear-gradient(135deg, #F5A623 0%, #FFD93D 100%);
  color: #78350F;
  box-shadow: 0 2px 8px rgba(245, 166, 35, 0.4);
}

.role-pharmacist {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Contáctanos header button */
.contact-header-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 9999px;
  cursor: pointer;
  color: white;
  font-size: 0.8125rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.contact-header-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.45);
}

/* User menu button */
.user-menu-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 9999px;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
}

.user-menu-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.4);
}

.user-avatar {
  background: white !important;
  color: #003DA5 !important;
  font-weight: 600;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 500;
}

/* Mobile menu button */
.mobile-menu-btn {
  color: white !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

.mobile-menu-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

/* Login button */
.login-btn {
  background: white !important;
  color: #003DA5 !important;
  border: none !important;
  font-weight: 600;
}

.login-btn:hover {
  background: rgba(255, 255, 255, 0.9) !important;
}

/* Mobile drawer nav items */
.app-header nav button {
  transition: all var(--duration-fast, 150ms) var(--ease-out);
  border: none;
  background: transparent;
  cursor: pointer;
}

.app-header nav button:hover {
  transform: translateX(4px);
}

.text-pg-blue {
  color: var(--color-pg-blue, #003DA5);
}

.bg-pg-blue\/10 {
  background-color: rgba(0, 61, 165, 0.1);
}

@media (min-width: 640px) {
  .header-content {
    padding: 0.875rem 1.5rem;
  }
}

@media (max-width: 639px) {
  .logo-image {
    width: 44px;
    height: 44px;
  }

  .logo-text {
    display: none;
  }
}

@media (min-width: 1024px) {
  .header-content {
    padding: 1rem 2rem;
  }
}
</style>
